<template>
  <dashboard-layout>
    <template #header-left>
      <div class="head">
        <i class="el-icon-arrow-left" @click="$router.push('/admin')"></i>
        <div>
          <b>提示词管理</b>
          <small>所有提示词统一在此编辑，保存即生效。默认值随包，编辑后存数据库覆盖。</small>
        </div>
      </div>
    </template>

    <div class="prompt-admin">
      <div class="toolbar">
        <span class="tip">共 {{ items.length }} 项 · 已覆盖 {{ overriddenCount }} 项。改完对话链路即时生效；定时/审图进程需重启后生效。</span>
        <div class="actions">
          <el-button size="small" round icon="el-icon-download" @click="exportAll">导出全部</el-button>
          <el-button size="small" round icon="el-icon-upload2" @click="triggerImport">导入</el-button>
          <input ref="fileInput" type="file" accept=".json,application/json" style="display:none" @change="onImportFile" />
          <el-button size="small" round icon="el-icon-refresh" @click="load">刷新</el-button>
        </div>
      </div>

      <div class="group-tabs">
        <button
          v-for="g in groups"
          :key="g.name"
          class="gtab"
          :class="{ on: g.name === activeGroup }"
          @click="activeGroup = g.name"
        >
          {{ g.name }}<span class="n">{{ g.items.length }}</span>
          <i v-if="g.dirty" class="dot"></i>
        </button>
      </div>

      <div v-loading="loading" class="group-body">
        <div v-for="it in activeItems" :key="it.key" class="item">
          <div class="item-head">
            <div class="meta">
              <b>{{ it.label }}</b>
              <code>{{ it.key }}</code>
              <el-tag size="mini" :type="it.is_overridden ? 'warning' : 'info'" effect="plain">
                {{ it.is_overridden ? "已覆盖" : "默认" }}
              </el-tag>
            </div>
            <div class="btns">
              <el-button size="mini" type="primary" round :loading="saving === it.key" @click="save(it)">保存</el-button>
              <el-button size="mini" round :disabled="!it.is_overridden" :loading="resetting === it.key" @click="reset(it)">重置为默认</el-button>
            </div>
          </div>
          <el-input type="textarea" v-model="it.value" :rows="rowsFor(it.value)" :class="{ dirty: it.value !== it._orig }" />
        </div>
        <p v-if="!activeItems.length && !loading" class="empty">这个分组暂无提示词。</p>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { adminApi } from "@/services/api";
import DashboardLayout from "@/components/DashboardLayout.vue";

export default {
  name: "PromptAdminView",
  components: { DashboardLayout },
  data() {
    return { items: [], loading: false, saving: "", resetting: "", activeGroup: "" };
  },
  created() {
    this.load();
  },
  computed: {
    overriddenCount() {
      return this.items.filter((i) => i.is_overridden).length;
    },
    groups() {
      const map = {};
      this.items.forEach((it) => {
        (map[it.group] = map[it.group] || []).push(it);
      });
      return Object.keys(map).map((name) => ({
        name,
        items: map[name],
        dirty: map[name].some((i) => i.value !== i._orig),
      }));
    },
    activeItems() {
      const g = this.groups.find((x) => x.name === this.activeGroup);
      return g ? g.items : [];
    },
  },
  methods: {
    rowsFor(v) {
      const n = (v || "").split("\n").length;
      return Math.min(Math.max(n + 1, 4), 16);
    },
    async load() {
      this.loading = true;
      try {
        const list = await adminApi.listPrompts();
        this.items = (list || []).map((i) => ({ ...i, _orig: i.value }));
        if (!this.activeGroup || !this.groups.some((g) => g.name === this.activeGroup)) {
          this.activeGroup = this.groups.length ? this.groups[0].name : "";
        }
      } catch (e) {
        this.$message.error(e.message || "加载失败");
      } finally {
        this.loading = false;
      }
    },
    async save(it) {
      this.saving = it.key;
      try {
        const updated = await adminApi.updatePrompt(it.key, it.value);
        Object.assign(it, updated, { _orig: updated.value });
        this.$message.success(`已保存：${it.label}`);
      } catch (e) {
        this.$message.error(e.message || "保存失败");
      } finally {
        this.saving = "";
      }
    },
    async reset(it) {
      try {
        await this.$confirm(`把「${it.label}」重置为随包默认值？`, "重置", { type: "warning" });
      } catch (e) {
        return;
      }
      this.resetting = it.key;
      try {
        const updated = await adminApi.resetPrompt(it.key);
        Object.assign(it, updated, { _orig: updated.value });
        this.$message.success("已重置为默认");
      } catch (e) {
        this.$message.error(e.message || "重置失败");
      } finally {
        this.resetting = "";
      }
    },
    async exportAll() {
      try {
        const data = await adminApi.exportPrompts();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `prompts-overrides-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        this.$message.success(`已导出 ${Object.keys(data || {}).length} 条覆盖`);
      } catch (e) {
        this.$message.error(e.message || "导出失败");
      }
    },
    triggerImport() {
      this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    },
    async onImportFile(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      let overrides;
      try {
        overrides = JSON.parse(await file.text());
      } catch (e) {
        this.$message.error("文件不是合法 JSON");
        return;
      }
      const count = Object.keys(overrides || {}).length;
      try {
        await this.$confirm(`将导入 ${count} 条提示词覆盖，覆盖同名项。继续？`, "导入", { type: "warning" });
      } catch (e) {
        return;
      }
      try {
        const res = await adminApi.importPrompts(overrides);
        this.$message.success(`已导入 ${res.applied} 条`);
        this.load();
      } catch (e) {
        this.$message.error(e.message || "导入失败");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.head {
  display: flex;
  align-items: center;
  gap: 10px;
  i { font-size: 18px; color: #999; cursor: pointer; }
  b { display: block; font-size: 16px; }
  small { color: #bbb; font-size: 12px; }
}
.prompt-admin {
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: wrap;
    .tip { color: #999; font-size: 13px; }
    .actions { display: flex; gap: 8px; }
  }
  .group-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
    .gtab {
      position: relative;
      border: 1px solid #e8e3ed;
      background: #fff;
      color: #666;
      border-radius: 16px;
      padding: 6px 14px;
      font-size: 13px;
      cursor: pointer;
      transition: 0.2s;
      .n { margin-left: 6px; color: #bbb; font-size: 12px; }
      .dot { position: absolute; top: 3px; right: 5px; width: 6px; height: 6px; border-radius: 50%; background: #7255e9; }
      &:hover { border-color: #ded6ff; }
      &.on {
        color: #fff;
        border-color: transparent;
        background: linear-gradient(90deg, #c94225, #7255e9);
        .n { color: rgba(255, 255, 255, 0.85); }
      }
    }
  }
  .group-body .empty { color: #bbb; text-align: center; padding: 30px 0; }
  .item {
    padding: 12px 0;
    border-top: 1px solid #f2f2f4;
    &:first-child { border-top: 0; }
    .item-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 8px;
      flex-wrap: wrap;
      .meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        b { font-size: 14px; }
        code { color: #816bef; background: #f7f4ff; padding: 1px 6px; border-radius: 6px; font-size: 12px; }
      }
      .btns { display: flex; gap: 8px; }
    }
    ::v-deep .el-textarea__inner { font-family: inherit; line-height: 1.6; }
    ::v-deep .dirty .el-textarea__inner { border-color: #c8bdf6; }
  }
}
</style>

<style scoped lang="scss">
.head {
  min-width: 0;

  i,
  small {
    color: var(--xyo-muted);
  }

  b {
    color: var(--xyo-ink-strong);
  }
}

.prompt-admin {
  min-width: 0;

  .toolbar {
    min-width: 0;

    .tip {
      min-width: 0;
      color: var(--xyo-muted);
    }

    .actions {
      min-width: 0;
      flex-wrap: wrap;
    }
  }

  .group-tabs {
    min-width: 0;

    .gtab {
      color: var(--xyo-ink-soft);
      border-color: var(--xyo-line);
      background: var(--xyo-surface-raised);

      .n {
        color: var(--xyo-muted);
      }

      .dot {
        background: var(--xyo-primary);
      }

      &:hover {
        color: var(--xyo-primary);
        border-color: var(--xyo-primary);
      }

      &.on {
        color: #fff;
        border-color: var(--xyo-primary);
        background: var(--xyo-primary);

        .n {
          color: rgba(255, 255, 255, 0.85);
        }
      }
    }
  }

  .group-body {
    min-width: 0;

    .empty {
      color: var(--xyo-muted);
    }
  }

  .item {
    min-width: 0;
    border-top-color: var(--xyo-line);

    .item-head {
      min-width: 0;

      .meta {
        min-width: 0;

        b {
          color: var(--xyo-ink-strong);
        }

        code {
          max-width: 100%;
          overflow-wrap: anywhere;
          color: var(--xyo-primary);
          background: var(--xyo-accent-pale);
        }
      }

      .btns {
        min-width: 0;
        flex-wrap: wrap;
      }
    }

    ::v-deep .dirty .el-textarea__inner {
      border-color: var(--xyo-primary);
      box-shadow: 0 0 0 2px var(--xyo-accent-pale);
    }
  }
}

@media (max-width: 768px) {
  .head,
  .head > div,
  .prompt-admin,
  .prompt-admin .toolbar,
  .prompt-admin .toolbar .tip,
  .prompt-admin .toolbar .actions,
  .prompt-admin .group-tabs,
  .prompt-admin .group-body,
  .prompt-admin .item,
  .prompt-admin .item .item-head,
  .prompt-admin .item .item-head .meta,
  .prompt-admin .item .item-head .btns {
    min-width: 0;
    max-width: 100%;
  }

  .prompt-admin .group-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
  }
}
</style>
